import {
  addHours,
  endOfDay,
  isBefore,
  isWithinInterval,
  startOfDay,
} from 'date-fns'
import momentObj from 'moment'
import { queryKeys } from '@opallabs/in-market-preview'

export const TEXT_COLUMN = 3 // which column index to place the "No Moments" badge

const defaultSorts = [
  {
    title: 'Time of Day',
    field: 'day_segment',
    pivots: ['null-value', 'morning', 'afternoon', 'evening', 'night'],
    showEmptyFields: true,
  },
  {
    title: 'Channel',
    field: 'services',
    pivots: ['null-value'],
    showEmptyFields: true,
  },
]

// Convert a label set to a sort option
export const labelSetToSortOption = (labelSet) => ({
  id: labelSet.id,
  field: labelSet.name,
  pivots: (labelSet.labels || [])
    .sort((a, b) => a.position - b.position)
    .map((l) => l.name)
    .concat(['null-value']),
  title: labelSet.name,
  type: labelSet.type,
})

export const sortOptions = (labelSets, initialSort = null) => {
  // Map labelSet objects to sort object
  const labelSetSorts = (labelSets || []).map((labelSet) =>
    labelSetToSortOption(labelSet)
  )

  // Ensure that initial sort value is included in the sort options
  // This handles the case where a user has a label set selected, and changes
  // weeks to a week with no moments in this label set.
  // Note that this changes state of initialSort, which is state.initialSort
  if (
    initialSort &&
    !defaultSorts.map((x) => x.field).includes(initialSort.field)
  ) {
    if (
      !labelSetSorts.map((labelSet) => labelSet.id).includes(initialSort.id)
    ) {
      labelSetSorts.push(initialSort)
      labelSetSorts.sort((a, b) => a.id - b.id)
    }
  }

  return [...defaultSorts, ...labelSetSorts]
}

export const formatSelectedSort = (labelSets, selectedSort) => {
  if (!selectedSort || !labelSets) {
    return selectedSort
  }

  // If selectedSort belongs to default sorts, return without modifying
  if (defaultSorts.map((x) => x.field).includes(selectedSort.field)) {
    return selectedSort
  }

  // Ensure that initial sort value is included in the sort options
  // This handles the case where a user has a label set selected, and changes
  // weeks to a week with no moments in this label set.
  // Note that this changes state of initialSort, which is state.initialSort
  const formattedSort = {
    id: selectedSort.id,
    field: selectedSort.field,
    title: selectedSort.title,
    type: selectedSort.type,
  }

  if (!labelSets.map((labelSet) => labelSet.id).includes(formattedSort.id)) {
    formattedSort.pivots = ['null-value']
    formattedSort.showEmptyFields = true
  } else {
    formattedSort.pivots = selectedSort.pivots
    formattedSort.showEmptyFields = false
  }
  return formattedSort
}

export const getDefaultSort = () => defaultSorts[0]

// Ensure that the selected sort is actually part of the labelSets
// It is possible that label sets are null and sort is still valid.
// Because of this, we'll validate by checking that if label sets are present, that the
// selected sort belongs to the label sets.
export const validateSort = (selectedSort, labelSets) => {
  if (!selectedSort) return false
  if (defaultSorts.map((sort) => sort.field).includes(selectedSort.field))
    return true
  if (
    labelSets &&
    labelSets.length > 0 &&
    !labelSets.find((labelSet) => labelSet.id === selectedSort.id)
  ) {
    return false
  }
  return true
}

export const isDropTarget = (date, draggedMoment) => {
  if (draggedMoment && draggedMoment.primaryStoryInterval) {
    const { start, end } = draggedMoment.primaryStoryInterval
    return isWithinInterval(date, { start, end })
  }
  return true
}

export const isValidSort = (sort, labelSets) => {
  if (!sort || sort.type !== 'label-sets') return true
  return labelSets.map((ls) => ls.id).includes(sort.id)
}

export const isWithinStoryDateRange = (date, story) => {
  if (!story || (!story.scheduled_start_at && !story.scheduled_end_at))
    return true
  const modifiedDate = addHours(date, 8)
  const start = startOfDay(story.scheduled_start_at)
  const end = endOfDay(story.scheduled_end_at)

  return isWithinInterval(modifiedDate, { start, end })
}

/*
 * Builds a new moment utilized as the "destination" when
 * moving content.
 */
export const buildDestinationMoment = (contentList, moment) => ({
  ...moment,
  isUpdatingContent: true,
  name: moment.title,
  posts: contentList,
  postCount: contentList.length,
})

/*
 * Builds a new moment with the specified content list.
 */
export const buildOriginMoment = (contentList, moment) => ({
  ...moment,
  name: moment.title,
  posts: contentList,
  postCount: contentList.length,
})

/*
 * Takes a list of content and determines which of them
 * need to be updated. Returns a promise which is
 * resolved once all content has finished updating.
 */
export const updatePendingContent = (
  contentListToUpdate,
  originMoment,
  updateContent
) => {
  const contentPromises = contentListToUpdate.map((contentItem) => {
    if (contentItem.shouldUpdate && contentItem.id) {
      return new Promise((resolve) => {
        updateContent({
          contentId: contentItem.id,
          params: {
            position: contentItem.position,
            moment_id: contentItem.moment.id,
          },
        })
          .then(() => resolve())
          .catch((err) => {
            throw err
          })
      })
    }
    return true
  })
  return Promise.all(contentPromises)
}

export function invalidateInMarketQuery(queryClient, workspace_id) {
  queryClient.invalidateQueries(queryKeys.inMarketContent({ workspace_id }))
}
/*
 * Builds all options utilized in the right click/context
 * menu for Moments and Content.
 */
export const buildContextMenuCallbacks = ({
  activateModalState,
  duplicateContent,
  launchMomentDuplicator,
  onAddToBoard,
  onAddToPlan,
  onDeleteEntity,
  updateContent,
  onUpdateMoment,
  queryClient,
  workspace_id,
}) => ({
  changeDaySegment: (momentId, newDaySegment) =>
    onUpdateMoment({
      momentId,
      update: { day_segment: newDaySegment },
    }),
  deleteContent: (id, moment) =>
    onDeleteEntity({
      entityType: 'content',
      activeModalKey: 'delete',
      params: { id, moment },
      withConfirmation: true,
    }),
  deleteMoment: (momentId, withConfirmation) =>
    onDeleteEntity({
      entityType: 'moment',
      activeModalKey: 'delete',
      params: { id: momentId },
      withConfirmation,
    }),
  duplicateContent: (contentItem, moment, pivotName) =>
    duplicateContent(contentItem, moment, pivotName),
  duplicateMoment: (momentId) => launchMomentDuplicator(momentId),
  renameContent: (contentId, newName) =>
    updateContent({
      contentId,
      callback: () => invalidateInMarketQuery(queryClient, workspace_id),
      params: {
        name: newName,
      },
    }),
  renameMoment: (momentId, newName) =>
    onUpdateMoment({
      momentId,
      update: { name: newName, title: newName },
    }),
  onAddToBoard,
  onAddToPlan,
  pickMomentCoverAsset: (moment) =>
    activateModalState({
      entityType: 'moment',
      activeModalKey: 'asset',
      params: moment,
    }),
})

/* The idea behind the multisorter is to be able to sort on a hierarchical list of parameters
the idea is to call it like array.multiSorter(options), where options is an array e.g.:
  [
    { sortBy: 'param1', nested: 'props', order: 'DESC' },
    { sortBy: 'param2', nested: null, order: 'DESC', type: 'dateString' },
    { sortBy: 'param3', nested: null, order: 'ASC', type: 'dateString' },
    ...
  ]
sortBy: parameter of the object to sort on
nested: if that parameter is one level deep in the sortable object, e.g. in 'x.props'
order: 'ASC' or 'DESC'
type: null or 'dateString' or 'momentObj'
  (required because a datestring cannot be reliably differentiated from any other string)
The sorter can accept datestrings and moment date objects because of our current reliance on both.
*/

export const multiSorter = (options) => (a, b) => {
  // 'for' loop to allow early return
  for (let i = 0; i < options.length; i += 1) {
    const property = options[i].sortBy

    if (options[i].nested) {
      const nested = options[i].nested
      if (a[nested][property] !== b[nested][property]) {
        let result = a[nested][property] - b[nested][property]
        if (options[i].type === 'dateString') {
          result = isBefore(a[nested][property], b[nested][property]) ? -1 : 1
        }
        if (options[i].type === 'momentObj') {
          result = momentObj(a[nested][property]).isBefore(b[nested][property])
            ? -1
            : 1
        }
        return options[i].order === 'DESC' ? -result : result
      }
    } else if (a[property] !== b[property]) {
      let result = a[property] - b[property]
      if (options[i].type === 'dateString') {
        result = isBefore(a[property], b[property]) ? -1 : 1
      }
      if (options[i].type === 'momentObj') {
        result = momentObj(a[property]).isBefore(b[property]) ? -1 : 1
      }
      return options[i].order === 'DESC' ? -result : result
    }
  }
  return 0
}
